import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Warning, PaperPlaneRight, Info, MapPin, CheckCircle } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { AvatarDisplay } from "@/components/Avatar";
import { detectFinderLang, getFinderT } from "@/finderI18n";
import api from "@/lib/api";

export default function Finder() {
  const { code } = useParams();
  const preview = ["1", "true"].includes(new URLSearchParams(window.location.search).get("preview"));
  const finderLang = detectFinderLang();
  const baseT = getFinderT(finderLang);
  const [finderOverrides, setFinderOverrides] = useState({});
  const t = (k) => finderOverrides[k] ? finderOverrides[k] : baseT(k);
  useEffect(() => { api.get("/content").then((r) => { const c = r.data && r.data[finderLang]; setFinderOverrides((c && c.finder) || {}); }).catch(() => {}); }, [finderLang]);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [msg, setMsg] = useState({ finder_name: "", message: "", found_location: "", finder_phone: "" });
  const [sent, setSent] = useState(false);
  const [loc, setLoc] = useState(null);
  const [locModal, setLocModal] = useState(false);
  const [myGroups, setMyGroups] = useState([]);
  useEffect(() => {
    if (!localStorage.getItem("sb_token")) return;
    api.get("/groups/mine").then((r) => setMyGroups((r.data || []).filter((g) => g.is_leader))).catch(() => {});
  }, []);
  const addToGroup = async (gid) => {
    try { await api.post(`/groups/${gid}/request-tag`, { code }); toast.success("Anfrage gesendet – der Besitzer muss bestätigen"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const confirmShareLocation = () => {
    setLocModal(false);
    if (!navigator.geolocation) { toast.error(t("locationError")); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success(t("locationAttached")); },
      () => toast.error(t("locationError")),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  useEffect(() => {
    document.documentElement.lang = finderLang;
    const applyTheme = (m, c) => {
      ["lavender", "mint", "peach", "sky", "pink", "petrol", "navy", "graphit", "forest", "bordeaux"].forEach((th) => document.body.classList.remove(`theme-${th}`));
      document.body.classList.remove("mode-classic", "mode-modern");
      document.body.classList.add(`mode-${m}`, `theme-${c}`);
    };
    api.get(`/public/tag/${code}${preview ? "?preview=true" : ""}`).then((r) => {
      setData(r.data);
      applyTheme(r.data.theme_mode === "modern" ? "modern" : "classic", r.data.theme_color || "lavender");
    }).catch(() => { setNotFound(true); applyTheme("classic", "lavender"); });
  }, [code]);

  const call = async (idx, phone) => {
    if (!revealed[idx]) {
      setRevealed({ ...revealed, [idx]: true });
      if (!preview) { try { await api.post(`/public/tag/${code}/call`); } catch {} }
      return;
    }
    window.location.href = `tel:${phone.replace(/\s/g, "")}`;
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.message.trim()) return;
    if (preview) { setSent(true); toast.success(t("messageSent")); return; }
    try {
      await api.post(`/public/tag/${code}/message`, { ...msg, ...(loc || {}) });
      setSent(true);
      toast.success(t("messageSent"));
    } catch { toast.error(t("genericError")); }
  };

  const mask = (phone) => phone.replace(/\d(?=\d{2})/g, "•");

  if (notFound || (data && !data.available)) {
    return (
      <div className="mx-auto max-w-md min-h-[100dvh] flex flex-col items-center justify-center px-6 gap-4 text-center" style={{ background: "var(--app-bg)" }}>
        <Logo className="h-14" />
        {data?.blocked ? (
          <div className="sb-card p-6" style={{ background: "#F3F4F6" }} data-testid="finder-blocked">
            <Warning size={48} weight="duotone" color="#6B7280" className="mx-auto mb-2" />
            <p className="font-heading font-semibold text-lg text-gray-700">{t("tagBlocked")}</p>
          </div>
        ) : data?.deactivated ? (
          <div className="sb-card p-6" data-testid="finder-deactivated">
            <Info size={48} weight="duotone" color="#F59E0B" className="mx-auto mb-2" />
            <p className="font-heading font-semibold text-lg text-gray-700">{t("deactivatedNotice")}</p>
          </div>
        ) : (
          <p className="font-body text-gray-600">{t("notAvailable")}</p>
        )}
      </div>
    );
  }

  if (!data) return <div className="mx-auto max-w-md min-h-[100dvh] flex items-center justify-center" style={{ background: "var(--app-bg)" }}><Logo className="h-14 animate-pulse" /></div>;

  return (
    <div className="mx-auto max-w-md min-h-[100dvh] px-5 py-6 sm:border-x-2 sm:border-gray-800" style={{ background: "var(--app-bg)" }}>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        {preview && (
          <div data-testid="preview-banner" className="sb-card p-3 mb-4 text-center font-heading text-sm text-gray-800" style={{ background: "#FEF9C3" }}>
            👀 {t("previewBanner")}
          </div>
        )}

        <div className="flex flex-col items-center text-center mb-5">
          <Logo className="h-8 mb-3" />
          <AvatarDisplay type={data.avatar_type} value={data.avatar_value} size={80} />
          <h1 className="text-xl font-heading font-semibold text-gray-900 mt-2">{data.first_name}</h1>
          {data.label && <p className="text-sm font-heading font-medium" style={{ color: "var(--app-strong)" }}>{data.label}</p>}
          {data.allow_calls !== false && (
            <span data-testid="finder-status-badge" className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-xs font-heading font-semibold"
              style={{ background: "color-mix(in srgb, var(--app-primary) 14%, #fff)", color: "var(--app-strong)" }}>
              <Phone size={13} weight="fill" />{t("statusCallable")}
            </span>
          )}
          <p className="font-body text-gray-600 mt-2 text-sm leading-snug">{t("finderIntro")}</p>
        </div>

        <div className="space-y-2.5">
          {data.contacts.map((c, i) => (
            <div key={i} className="sb-card p-4" data-testid={`finder-contact-${i}`}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-heading font-semibold text-gray-900">{c.name}</p>
                {c.relationship && <p className="text-xs font-body text-gray-500 shrink-0">{c.relationship}</p>}
              </div>
              <p className="font-body text-gray-700 mt-0.5 tracking-wide text-lg">{revealed[i] ? c.phone : mask(c.phone)}</p>
              {data.allow_calls !== false ? (
                <>
                  <button data-testid={`finder-call-${i}`} onClick={() => call(i, c.phone)}
                    className="sb-btn w-full py-3.5 mt-2.5 text-white font-medium flex items-center justify-center gap-2" style={{ background: "var(--app-primary)" }}>
                    <Phone size={20} weight="fill" />{revealed[i] ? t("callNow") : t("revealCall")}
                  </button>
                  {!revealed[i] && <p className="text-[11px] font-body text-gray-400 mt-1.5 text-center">{t("revealHint")}</p>}
                </>
              ) : (
                <p className="text-xs font-body text-gray-500 mt-2">{c.phone}</p>
              )}
            </div>
          ))}
          {data.contacts.length === 0 && <p className="font-body text-gray-500 text-sm">—</p>}
        </div>

        {data.allow_messages === false ? null : sent ? (
          <div className="sb-card p-5 text-center mt-5" data-testid="finder-message-sent"><p className="font-heading text-gray-700">{t("messageSent")}</p></div>
        ) : (
          <form onSubmit={sendMessage} className="sb-card p-4 space-y-2.5 mt-5">
            <h2 className="text-sm font-heading font-semibold text-gray-900">{t("leaveMessage")}</h2>
            <p className="text-xs font-body text-gray-500 -mt-1 leading-snug">{t("msgFormHint")}</p>
            <input data-testid="finder-name-input" className="sb-input" placeholder={t("yourName")} value={msg.finder_name} onChange={(e) => setMsg({ ...msg, finder_name: e.target.value })} />
            <textarea data-testid="finder-message-input" className="sb-input" rows={3} placeholder={t("yourMessage")} value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })} required />
            <input data-testid="finder-location-input" className="sb-input" placeholder={t("foundLocation")} value={msg.found_location} onChange={(e) => setMsg({ ...msg, found_location: e.target.value })} />
            <input data-testid="finder-phone-input" className="sb-input" placeholder={t("finderPhone")} value={msg.finder_phone} onChange={(e) => setMsg({ ...msg, finder_phone: e.target.value })} />
            <button type="button" data-testid="finder-share-location" onClick={() => { if (!loc) setLocModal(true); }}
              className="sb-btn w-full py-3 font-medium flex items-center justify-center gap-2 text-sm"
              style={loc ? { background: "var(--app-primary)", color: "#fff" } : { background: "#fff" }}>
              {loc ? <><CheckCircle size={18} weight="fill" />{t("locationConfirmed")}</> : <><MapPin size={18} weight="bold" />📍 {t("shareLocation")}</>}
            </button>
            <button data-testid="finder-send-message" type="submit" className="sb-btn w-full py-3.5 text-white font-medium flex items-center justify-center gap-2" style={{ background: "var(--app-primary)" }}>
              <PaperPlaneRight size={20} weight="fill" />{t("sendMessage")}
            </button>
          </form>
        )}

        {myGroups.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200" data-testid="finder-add-to-group">
            <p className="font-body text-xs text-gray-400 mb-2">{t("addToGroupLabel")}</p>
            <div className="flex flex-wrap gap-2">
              {myGroups.map((g) => (
                <button key={g.id} data-testid={`add-to-group-${g.id}`} onClick={() => addToGroup(g.id)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-heading font-medium active:scale-95 transition-transform">+ {g.name}</button>
              ))}
            </div>
          </div>
        )}
        <p className="text-center text-xs font-body text-gray-400 mt-8">{t("poweredBy")}</p>
      </motion.div>

      {locModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-testid="location-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setLocModal(false)} />
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            className="relative w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "color-mix(in srgb, var(--app-primary) 14%, #fff)" }}>
              <MapPin size={24} weight="duotone" style={{ color: "var(--app-strong)" }} />
            </div>
            <h3 className="text-lg font-heading font-semibold text-gray-900">{t("locModalTitle")}</h3>
            <p className="font-body text-sm text-gray-600 mt-1.5 leading-relaxed">{t("locModalText")}</p>
            <p className="font-body text-xs text-gray-400 mt-2">{t("locModalHint")}</p>
            <div className="flex gap-2 mt-5">
              <button data-testid="location-modal-cancel" onClick={() => setLocModal(false)} className="sb-btn flex-1 py-3 bg-white font-medium">{t("cancel")}</button>
              <button data-testid="location-modal-confirm" onClick={confirmShareLocation} className="sb-btn flex-1 py-3 text-white font-medium" style={{ background: "var(--app-primary)" }}>{t("locModalConfirm")}</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
